import { City, Country } from "country-state-city";

export interface CityData {
  name?: string;
  countryCode?: string;
}

/** Option for react-select AsyncSelect (registration checkout / explorer, etc.). */
export interface CityOption {
  value: string;
  label: string;
  city: string;
  country: string;
}

/**
 * If city holds a combined "City, Country" label (e.g. Explorer.City from API),
 * split into separate values. Country parsed from the city string wins over a
 * stale/explicit country (same idea as getCountryFlagUrl combo priority).
 * When city has no comma, the explicit country is kept.
 */
export function splitCityCountry(
  city?: string | null,
  country?: string | null,
): { city: string; country: string } {
  const rawCity = (city ?? "").trim();
  const rawCountry = (country ?? "").trim();
  if (!rawCity.includes(",")) {
    return { city: rawCity, country: rawCountry };
  }
  const i = rawCity.lastIndexOf(",");
  return {
    city: rawCity.slice(0, i).trim(),
    country: rawCity.slice(i + 1).trim() || rawCountry,
  };
}

/** Display label with both city and country when available (no duplicate country). */
export function formatCityCountryLabel(
  city?: string | null,
  country?: string | null,
): string {
  const { city: c, country: co } = splitCityCountry(city, country);
  return [c, co].filter(Boolean).join(", ");
}

let citySearchTimeout: ReturnType<typeof setTimeout> | null = null;

/** Search cities worldwide; labels are "City, Country" (same as PersonalInfoStep checkout). */
export function loadCityOptions(
  inputValue: string,
  callback: (options: readonly CityOption[]) => void,
): void {
  if (!inputValue || inputValue.trim().length === 0) {
    callback([]);
    return;
  }

  if (citySearchTimeout) {
    clearTimeout(citySearchTimeout);
  }

  citySearchTimeout = setTimeout(() => {
    try {
      const searchTerm = inputValue.trim().toLowerCase();
      const allCitiesData = City.getAllCities();
      const countryMap = new Map(
        Country.getAllCountries().map((c) => [c.isoCode, c.name]),
      );

      const filtered: CityOption[] = allCitiesData
        .filter((c: CityData) => {
          const cityName = c.name?.toLowerCase() || "";
          return cityName.includes(searchTerm);
        })
        .slice(0, 30)
        .map((c: CityData) => {
          const countryName =
            countryMap.get(c.countryCode || "") || c.countryCode || "";
          const cityName = c.name || "";
          return {
            value: cityName,
            label: `${cityName}, ${countryName}`,
            city: cityName,
            country: countryName,
          };
        });

      callback(filtered);
    } catch (e) {
      console.error("Error searching cities:", e);
      callback([]);
    }
  }, 200);
}
